import { DestroyRef, Signal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, of, switchMap } from 'rxjs';
import { ProgramTypes } from '@k12/models';
import { EnrollmentApplyService } from './enrollment-apply.service';

/**
 * Inputs the enrollment-apply controller derives its state from. These are the OS/ESA detail
 * component's signal inputs, passed straight through so the controller reacts to them.
 */
export interface EnrollmentApplyInputs {
  /** The program the Apply button enrolls into (OS or ESA+). */
  programType: ProgramTypes;
  /** True only when there is no existing award record (the empty-state Apply card is shown). */
  showEmptyState: Signal<boolean>;
  /** The award year being viewed; used to probe/create the application. */
  academicYearId: Signal<number | null>;
  /** K12.Person id of the application subject (Application.OnBehalfOfPersonId). */
  personId: Signal<string | null>;
}

export interface EnrollmentApplyController {
  /** Whether the empty-state Apply button should be disabled. */
  applyDisabled: Signal<boolean>;
  /** Creates the enrollment application on behalf of the subject and navigates into the wizard. */
  apply(): void;
}

/**
 * Signal adapter over EnrollmentApplyService for the OS and ESA+ detail cards. Availability is a
 * single reactive stream: a distinct (eligible, year) key drives one probe at a time via switchMap
 * (in-flight probes are cancelled), so there are no redundant calls and no ordering race that could
 * leave the button silently disabled.
 *
 * Must be called from an injection context (a component field initializer) because it uses
 * `inject`, `toObservable`, and `toSignal`.
 */
export function createEnrollmentApply(inputs: EnrollmentApplyInputs): EnrollmentApplyController {
  const applyService = inject(EnrollmentApplyService);
  const destroyRef = inject(DestroyRef);

  // Bumped after a 422 so the same (eligible, year) re-probes even though its value is unchanged.
  const refreshTick = signal(0);

  const key = computed(() => ({
    eligible: inputs.showEmptyState() && inputs.academicYearId() != null && !!inputs.personId(),
    year: inputs.academicYearId(),
    tick: refreshTick()
  }));

  const periodOpen = toSignal(
    toObservable(key).pipe(
      distinctUntilChanged((a, b) => a.eligible === b.eligible && a.year === b.year && a.tick === b.tick),
      switchMap(({ eligible, year }) =>
        eligible && year != null ? applyService.isPeriodOpen(inputs.programType, year) : of(false)
      )
    ),
    { initialValue: false }
  );

  const applyDisabled = computed(() => !periodOpen());

  function apply(): void {
    const year = inputs.academicYearId();
    const personId = inputs.personId();
    if (year == null || !personId || !periodOpen()) {
      return;
    }

    applyService
      .apply(inputs.programType, year, personId)
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe((outcome) => {
        if (outcome === 'retry') {
          refreshTick.update((t) => t + 1);
        }
      });
  }

  return { applyDisabled, apply };
}
